import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore, usePremiumStore } from '../store';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

/**
 * AuthSuccess Component - Handles OAuth callback token
 * 
 * This page receives the token from the backend OAuth callback,
 * stores it in localStorage (on the correct domain), and redirects to dashboard.
 */
const AuthSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const fetchPremiumStatus = usePremiumStore((s) => s.fetchStatus);
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    const processToken = async () => {
      try {
        // Get token from URL
        const token = searchParams.get('token');
        
        // Debug info
        const apiUrl = window.location.origin + '/api';
        setDebugInfo(`API: ${apiUrl}`);
        console.log('[AuthSuccess] Current origin:', window.location.origin);
        console.log('[AuthSuccess] API URL will be:', apiUrl);
        
        if (!token) {
          setStatus('error');
          setError('Kein Token erhalten');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        console.log('[AuthSuccess] Token received:', token.substring(0, 20) + '...');
        
        // Store token in localStorage
        localStorage.setItem('mentis_session_token', token);
        console.log('[AuthSuccess] Token stored in localStorage');
        
        // Verify token is actually stored
        const storedToken = localStorage.getItem('mentis_session_token');
        if (storedToken !== token) {
          throw new Error('Token wurde nicht korrekt gespeichert');
        }
        console.log('[AuthSuccess] Token verified in localStorage');
        
        // Clear URL to hide token
        window.history.replaceState({}, '', '/auth/success');
        
        // Small delay to ensure localStorage is synced
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Verify the token works by checking auth
        setStatus('verifying');
        console.log('[AuthSuccess] Calling checkAuth with token...');
        
        // Make a direct API call to verify (bypassing store cache)
        const verifyResponse = await fetch(`${window.location.origin}/api/auth/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          },
          credentials: 'include'
        });
        
        console.log('[AuthSuccess] Direct verify response:', verifyResponse.status);
        
        if (verifyResponse.ok) {
          const userData = await verifyResponse.json();
          console.log('[AuthSuccess] User verified:', userData.email);
          
          // Now call checkAuth to update the store
          await checkAuth(true);
          
          // Fetch premium status after successful auth (non-blocking)
          fetchPremiumStatus().catch(() => {});
          
          setStatus('success');
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 500);
        } else {
          const errorData = await verifyResponse.text();
          console.error('[AuthSuccess] Verify failed:', verifyResponse.status, errorData);
          throw new Error(`Token-Verifizierung fehlgeschlagen (${verifyResponse.status})`);
        }
        
      } catch (err) {
        console.error('[AuthSuccess] Error:', err);
        setStatus('error');
        setError(err.message || 'Authentifizierung fehlgeschlagen');
        
        // Clear invalid token
        localStorage.removeItem('mentis_session_token');
        
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processToken();
  }, [searchParams, navigate, checkAuth]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        {status === 'processing' || status === 'verifying' ? (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Anmeldung wird verarbeitet...</h2>
            <p className="text-muted-foreground">Bitte warten</p>
            {debugInfo && <p className="text-xs text-muted-foreground mt-4 font-mono">{debugInfo}</p>}
          </>
        ) : status === 'success' ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Anmeldung erfolgreich!</h2>
            <p className="text-muted-foreground">Weiterleitung zum Dashboard...</p>
          </>
        ) : (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Anmeldung fehlgeschlagen</h2>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-2">Weiterleitung zur Anmeldung...</p>
            {debugInfo && <p className="text-xs text-muted-foreground mt-4 font-mono">{debugInfo}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthSuccess;
